import { isolateComponent, labelStyles, withModifier, } from '@zapier/style-encapsulation';
import { css } from '@emotion/react';
import { BrandButton, Button, Colors, DarkBackgroundButton, Heading, Text, Typography, Icon, TagLabel, Grid, } from '../../../../index';
import { SubscriptionFormWithDisclaimer } from '../../../forms/SubscriptionForm';
import { createUniqueCustomProperties } from '../../../../utils/createUniqueCustomProperty';
import { transformBackgroundColor, validateZapierBackgroundAccessibility, getSupportedCTAColor, } from '../utils';
import { breakpoints } from '../constants';
const Vars = createUniqueCustomProperties('HeroColorBlock', [
    'primaryBackgroundColorStop',
    'secondaryBackgroundColorStop',
]);
const SubscriptionFormStyles = {
    ctaContainer: css `
    max-width: 470px;
    @media screen and (min-width: ${breakpoints.tablet}px) {
      align-items: stretch;
      flex-direction: column;
    }
  `,
    ctaDisclaimer: css `
    margin: 10px 0;
    text-align: left;
  `,
};
const Styles = labelStyles('HeroColorBlock', {
    root: (backgroundColor = 'zapier') => css `
    padding: 20px 19.5px;
    ${Vars.primaryBackgroundColorStop}: ${Colors[backgroundColor]};
    ${Vars.secondaryBackgroundColorStop}: ${Colors.neutral100};
    background: linear-gradient(
      to bottom,
      var(${Vars.primaryBackgroundColorStop}) 80%,
      var(${Vars.secondaryBackgroundColorStop}) 20%
    );
    @media screen and (min-width: ${breakpoints.mobile}px) {
      padding: 40px 31px;
      background: linear-gradient(
        to bottom,
        var(${Vars.primaryBackgroundColorStop}) 75%,
        var(${Vars.secondaryBackgroundColorStop}) 25%
      );
    }
    @media screen and (min-width: ${breakpoints.tablet}px) {
      padding: 60px 31px;
      background: linear-gradient(
        to bottom,
        var(${Vars.primaryBackgroundColorStop}) 54.55%,
        var(${Vars.secondaryBackgroundColorStop}) 45.45%
      );
    }
  `,
    content: css `
    max-width: 770px;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    align-items: center;
  `,
    textContent: css `
    margin: auto;
  `,
    labelContainer: css `
    margin-bottom: 10px;
  `,
    heading: (backgroundColor) => css `
    ${Typography.pageHeader4}
    color: ${Colors[backgroundColor === 'zapier' ? 'cream' : 'neutral100']};
    margin-bottom: 10px;
    text-align: center;

    @media screen and (min-width: ${breakpoints.tablet}px) {
      ${Typography.pageHeader3}
      margin-bottom: 20px;
    }
  `,
    description: (backgroundColor) => css `
    ${Typography.pageHeader9}
    color: ${Colors[backgroundColor === 'zapier' ? 'cream' : 'neutral100']};
    text-align: center;
    margin: 0 auto;
    max-width: 570px;
  `,
    ctaContainer: (isSubscriptionForm = false) => css `
    margin-top: 20px;
    text-align: center;
    width: 100%;
    @media screen and (min-width: ${breakpoints.tablet}px) {
      margin-top: 40px;
    }
    ${isSubscriptionForm &&
        withModifier('subscription-form', SubscriptionFormStyles.ctaContainer)}
  `,
    ctaButtonWrapper: css `
    margin: 0 auto;

    // Button should take up full width in mobile only;
    button,
    a {
      width: 100%;
    }

    @media screen and (min-width: ${breakpoints.tablet}px) {
      button,
      a {
        width: auto;
      }
    }
  `,
    ctaDisclaimer: (isSubscriptionForm = false) => css `
    margin: 10px 0 0 0;
    text-align: center;
    ${isSubscriptionForm &&
        withModifier('subscription-form-disclaimer', SubscriptionFormStyles.ctaDisclaimer)}
  `,
    mediaContent: css `
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-top: 40px;
  `,
});
export const HeroColorBlock = ({ ctaColor = 'primary-alt', isSubscriptionForm = false, subscriptionPlaceholder = 'Email address', backgroundColor = 'zapier', heading, description, ctaText, ctaHref, ctaDisclaimer, onCtaClick, onCtaKeyDown, children, onSubscriptionFormSubmit, ctaTarget, label, labelIcon, labelColor, }) => {
    const mappedBackgroundColor = transformBackgroundColor(backgroundColor);
    validateZapierBackgroundAccessibility(mappedBackgroundColor, isSubscriptionForm, !!ctaDisclaimer);
    const getCtaComponent = () => {
        const sharedProps = {
            children: ctaText,
            href: !isSubscriptionForm ? ctaHref : undefined,
            onClick: onCtaClick,
            onKeyDown: onCtaKeyDown,
            type: isSubscriptionForm
                ? 'submit'
                : 'button',
            'data-testid': 'color-block-hero-cta-button',
            target: ctaTarget,
        };
        const color = getSupportedCTAColor(ctaColor, mappedBackgroundColor);
        return color === 'brand' ? (isolateComponent(BrandButton, Object.assign({}, sharedProps))) : color === 'primary-dark' ? (isolateComponent(DarkBackgroundButton, Object.assign({}, sharedProps))) : (isolateComponent(Button, Object.assign({}, sharedProps, { color: color, size: "small" })));
    };
    return (isolateComponent("div", { css: Styles.root(mappedBackgroundColor), "data-background-color": mappedBackgroundColor },
        isolateComponent("div", { css: Styles.content },
            label && (isolateComponent("div", { css: Styles.labelContainer },
                isolateComponent(TagLabel, { color: labelColor }, labelIcon ? (isolateComponent(Grid, { alignItems: "center", columns: "auto max-content", gap: 5 },
                    isolateComponent(Icon, { name: labelIcon, size: 20 }),
                    label)) : (label)))),
            isolateComponent(Heading, { css: Styles.heading(mappedBackgroundColor) }, heading),
            description && (isolateComponent("div", { css: Styles.description(mappedBackgroundColor) }, description)),
            ctaText && (ctaHref || onCtaClick || isSubscriptionForm) && (isolateComponent("div", { css: Styles.ctaContainer(isSubscriptionForm) }, isSubscriptionForm ? (isolateComponent(SubscriptionFormWithDisclaimer, { placeholderText: subscriptionPlaceholder, onSubmit: onSubscriptionFormSubmit, renderButton: () => getCtaComponent(), disclaimerTextColor: mappedBackgroundColor === 'neutral100'
                    ? 'primary'
                    : 'secondary' })) : (isolateComponent("div", null,
                isolateComponent("div", { css: Styles.ctaButtonWrapper }, getCtaComponent()),
                ctaDisclaimer && (isolateComponent("div", { css: Styles.ctaDisclaimer(isSubscriptionForm) },
                    isolateComponent(Text, { tag: "div", type: "paragraph3", color: mappedBackgroundColor === 'zapier'
                            ? 'cream'
                            : 'neutral100' }, ctaDisclaimer))))))),
            isolateComponent("div", { css: Styles.mediaContent }, children))));
};
//# sourceMappingURL=HeroColorBlock.js.map
// @zapier/design-system@5.32.5 (2004a9d50)
